import { ConfirmDialog } from '@/components/confirm/ConfirmDialog'
import type { PromptEvalGate } from '@/domain/prompt.types'

export interface PublishPromptDialogProps {
	open: boolean
	promptName: string
	/** Absent while the gate is still loading. */
	gate?: PromptEvalGate
	isLoadingGate?: boolean
	isPublishing?: boolean
	/** Set when the publish request itself failed, including a refusal by the gate. */
	errorMessage?: string | null
	onConfirm: () => void
	onCancel: () => void
}

/** The counts, laid out so a reader compares them without parsing a sentence. */
function EvalCounts({ gate }: { gate: PromptEvalGate }) {
	if (gate.summary === null) return null
	const { passed, failed, errored, ranAt } = gate.summary

	return (
		<div className="rounded-ds-card bg-ds-surface p-3">
			<dl className="flex gap-4 text-ds-sm">
				<div>
					<dt className="text-ds-text-muted">Passed</dt>
					<dd className="font-semibold text-ds-text">{passed}</dd>
				</div>
				<div>
					<dt className="text-ds-text-muted">Failed</dt>
					<dd className="font-semibold text-ds-text">{failed}</dd>
				</div>
				<div>
					<dt className="text-ds-text-muted">Errored</dt>
					<dd className="font-semibold text-ds-text">{errored}</dd>
				</div>
			</dl>
			<p className="mt-2 text-xs text-ds-text-muted">Last run {new Date(ranAt).toLocaleString()}</p>
		</div>
	)
}

/**
 * Confirm-before-publish, showing the last settled eval result.
 *
 * Publishing is the action that repoints what production traffic resolves to, so it
 * gets a deliberate confirmation rather than a single click. The eval result is shown
 * because it is the evidence the decision should rest on — but a warning does NOT
 * disable the button: the factory's model is that a human decides, and only the
 * tenant's blocking flag can actually refuse.
 *
 * A run still in flight is reported by the API as no result, so it warns like a
 * never-evaluated prompt rather than being read as a pass.
 *
 * The evidence is about the PUBLISHED version, not the pending one: an eval run
 * targets a version, and the change being published has none until this publish
 * creates it. Every line of copy here says so, because "the last evaluation passed"
 * on its own would be read as "this publish is safe" — a stronger claim than the
 * gate can support. Covering a pending change needs a run against an unpublished
 * body, which does not exist yet.
 */
export function PublishPromptDialog({
	open,
	promptName,
	gate,
	isLoadingGate,
	isPublishing,
	errorMessage,
	onConfirm,
	onCancel,
}: PublishPromptDialogProps) {
	const blocked = gate?.wouldBlock === true

	return (
		<ConfirmDialog
			open={open}
			title={`Publish “${promptName}”?`}
			description="This freezes the current body, variables and model configuration into a new immutable version."
			confirmLabel="Publish"
			busyLabel="Publishing…"
			submitting={isPublishing}
			// Disabled only when the tenant would genuinely refuse — a warning alone
			// must leave the human free to proceed.
			confirmDisabled={blocked}
			errorMessage={errorMessage}
			onConfirm={onConfirm}
			onCancel={onCancel}
		>
			<div className="space-y-3">
				{isLoadingGate ? (
					<p className="text-ds-sm text-ds-text-muted">Checking the published version's last evaluation…</p>
				) : null}

				{gate?.warning ? (
					<div
						// `alert` rather than plain text: a publisher scanning for the confirm
						// button should have this announced, not merely rendered near it.
						role="alert"
						className="rounded-ds-card border border-ds-warning bg-ds-surface p-3 text-ds-sm text-ds-text"
					>
						<p className="font-semibold text-ds-warning">{gate.warning}</p>
						{blocked ? (
							<p className="mt-1">
								This tenant requires a passing evaluation before publishing, so this publish will be refused.
							</p>
						) : (
							<p className="mt-1">
								You can still publish — the evaluation informs this decision, it does not block it.
							</p>
						)}
					</div>
				) : null}

				{gate && !gate.warning ? (
					<p className="text-ds-sm text-ds-success">The published version's last evaluation passed.</p>
				) : null}

				{gate ? <EvalCounts gate={gate} /> : null}

				{/*
					Stated plainly rather than left to be inferred: every figure above
					describes a PUBLISHED version, and the change being published has never
					been evaluated because an eval run targets a version and the pending
					body has none. Saying "the last evaluation passed" without this reads as
					"this publish is safe", which is a claim the gate cannot make.
				*/}
				<p className="text-ds-2xs text-ds-text-subtle">
					Evaluations run against published versions, so they do not cover the changes you are publishing now.
				</p>
			</div>
		</ConfirmDialog>
	)
}
